import '../css/stylus-root.mycss';
import './helpers/weekStartPolyfill';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { LangContextProvider } from './hooks/useLanguage';
import { ErrorBoundaryProvider } from './hooks/useError';
import { MarketingProvider } from './hooks/useMarketing';
import StripeContext from './payments/StripeContext';
import { parseSlugAndShowIdFromUrl } from './helpers/widgetSlugDomain';
import { CountdownContextProvider } from './hooks/useCountdown';
import { initSentry } from './sentry';
import SeatPickerIframe, { parseIframeParams } from './SeatPickerIframe';
import { applyPersistedStyling } from './helpers/showStyling';

// Initialize Sentry
initSentry();

let alreadyLoaded = false;

function loadRoot(rootElement: HTMLElement, showId: number, slugFromUrl?: string): void {
    if (alreadyLoaded) {
        console.warn('[FIREKNIFE] Widget already loaded, not loading again!');
        // trigger event to reopen the widget
        const event = new CustomEvent('fireknife-open-widget', {});
        document.dispatchEvent(event);
        return;
    }
    alreadyLoaded = true;
    applyPersistedStyling(rootElement, showId);
    const root = ReactDOM.createRoot(rootElement);
    root.render(
        <StrictMode>
            <ErrorBoundaryProvider>
                <MarketingProvider>
                    <StripeContext>
                        <LangContextProvider>
                            <CountdownContextProvider>
                                <App slug={slugFromUrl} showId={showId} />
                            </CountdownContextProvider>
                        </LangContextProvider>
                    </StripeContext>
                </MarketingProvider>
            </ErrorBoundaryProvider>
        </StrictMode>,
    );
}

// Extend the Window interface to include showFireknifeReservation
declare global {
    interface Window {
        showFireknifeReservation: (showId?: number, affiliateCode?: string) => void;
        hideFireknifeReservation: () => void;
    }
}

/**
 * Fireknife widget function to load the reservation widget.
 * @param showId - The show id to load. If not provided, it will be taken from the root element.
 * @param affiliateCode - Optional affiliate code to attribute the booking to.
 *   Also accepted via `data-affiliate` on the root element or `?affiliate=` in the URL.
 * @returns void
 * @example
 * window.showFireknifeReservation(123);
 * window.showFireknifeReservation(123, 'partner-code');
 * window.showFireknifeReservation();
 */
window.showFireknifeReservation = (showId?: number, affiliateCode?: string): void => {
    let showForApp: number | undefined = showId;
    let rootElement = document.getElementById('fireknife-widget-root');
    if (!rootElement) {
        document.body.insertAdjacentHTML('beforeend', '<div id="fireknife-widget-root"></div>');
    }
    rootElement = document.getElementById('fireknife-widget-root');
    const showIdFromRoot = rootElement?.getAttribute('data-show');
    if (showIdFromRoot !== '' && typeof showIdFromRoot === 'string' && showForApp === undefined) {
        showForApp = parseInt(showIdFromRoot, 10);
    }

    if (showForApp === undefined) {
        console.error(
            '[FIREKNIFE] No show id found on root element and no show id passed to showFireknifeReservation',
        );
        return;
    }

    // Mirror the function-arg affiliate code onto the root div so Step4 has
    // a single source of truth (data-affiliate) at submit. A pre-set
    // data-affiliate stays as-is when the arg is omitted. Step4 re-validates
    // the format before sending.
    if (affiliateCode) {
        rootElement?.setAttribute('data-affiliate', affiliateCode);
    }

    const loadParams = parseSlugAndShowIdFromUrl();
    if (loadParams.slug && loadParams.showId) {
        showForApp = loadParams.showId;
    }

    if (rootElement) {
        rootElement.setAttribute('data-show', showForApp.toString());
    }

    console.log('[FIREKNIFE] Loading reservation widget for show id', showForApp);
    loadRoot(rootElement!, showForApp!, loadParams?.slug);
};

/**
 * Fireknife widget function to hide the reservation widget.
 * @returns void
 * @example
 * window.hideFireknifeReservation();
 */
window.hideFireknifeReservation = (): void => {
    // fire a custom event on the document
    const event = new CustomEvent('fireknife-close-widget', {});
    document.dispatchEvent(event);
};

// Seat picker iframe mode: /seat-picker-iframe?showId=...&timeslotId=...&cart=...&seats=...
if (window.location.pathname.includes('/seat-picker-iframe')) {
    const iframeParams = parseIframeParams();
    if (iframeParams) {
        document.body.classList.add('fireknife-iframe-mode');
        let rootEl = document.getElementById('fireknife-widget-root');
        if (!rootEl) {
            document.body.insertAdjacentHTML('beforeend', '<div id="fireknife-widget-root"></div>');
            rootEl = document.getElementById('fireknife-widget-root');
        }
        const root = ReactDOM.createRoot(rootEl!);
        root.render(
            <StrictMode>
                <ErrorBoundaryProvider>
                    <LangContextProvider>
                        <SeatPickerIframe
                            showId={iframeParams.showId}
                            timeslotId={iframeParams.timeslotId}
                            initialCart={iframeParams.cart}
                        />
                    </LangContextProvider>
                </ErrorBoundaryProvider>
            </StrictMode>,
        );
    }
} else {
    // Normal widget loading
    const rootElementOnStart = document.getElementById('fireknife-widget-root')!;
    if (
        rootElementOnStart &&
        rootElementOnStart.hasAttribute('data-show') &&
        rootElementOnStart.getAttribute('data-visible') === 'visible'
    ) {
        window.showFireknifeReservation();
    }

    // we need to pop the widget if fireknife get url is present
    const urlParams = new URLSearchParams(window.location.search);
    const fireknifeGet = urlParams.get('fireknife');
    if (fireknifeGet) {
        window.showFireknifeReservation();
    }
}

// Detect and store UTM parameters in localStorage for booking attribution
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id'];
const utmSearchParams = new URLSearchParams(window.location.search);
const utmParams: Record<string, string> = {};
UTM_KEYS.forEach((key) => {
    const value = utmSearchParams.get(key);
    if (value) {
        utmParams[key] = value;
    }
});
if (Object.keys(utmParams).length > 0) {
    try {
        window.localStorage.setItem('utmParams', JSON.stringify(utmParams));
    } catch (e) {
        // localStorage may be unavailable; silently ignore
    }
}
